import { useEffect, useState } from "react";
import { List, Loader2, Map as MapIcon, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import RelayMap from "@/components/RelayMap";

const km = (m) => (m / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 1 });

function PointDetails({ p, index, selected, onSelect, open, setOpen }) {
  return (
    <label data-testid={`relay-point-${p.id}`} className={`flex cursor-pointer gap-2.5 rounded-lg border px-3 py-2 transition-colors ${selected ? "border-amber-deep bg-white" : "border-amber-900/10 bg-white/70 hover:border-amber-deep/50"}`}>
      <input type="radio" name="relay-point" checked={selected} onChange={() => onSelect(p)} className="mt-0.5 accent-amber-deep" />
      <span className="min-w-0 flex-1 text-xs text-bark-soft">
        <span className="block text-sm font-medium text-bark">{index + 1}. {p.name}</span>
        {p.address}, {p.postal_code} {p.city}
        {p.distance_m > 0 && <span className="text-stone-mute"> · {km(p.distance_m)} km</span>}
        <button type="button" data-testid={`relay-point-hours-${p.id}`} onClick={(e) => { e.preventDefault(); setOpen(open ? null : p.id); }} className="ml-1 text-amber-deep underline underline-offset-2">horaires</button>
        {open && <span className="mt-1 block space-y-0.5">{p.hours.map((h) => <span key={h} className="block">{h}</span>)}</span>}
      </span>
    </label>
  );
}

export default function RelayPointPicker({ postal, weight, selected, onSelect }) {
  const [points, setPoints] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [view, setView] = useState("map");

  useEffect(() => {
    setPoints(null); setError(""); onSelect(null);
    const t = setTimeout(() => {
      api.get("/mondial-relay/points", { params: { postal, weight } })
        .then(({ data }) => setPoints(data))
        .catch((err) => { setPoints([]); setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Recherche des Points Relais impossible"); });
    }, 300);
    return () => clearTimeout(t);
  }, [postal]); // eslint-disable-line react-hooks/exhaustive-deps

  const idx = points && selected ? points.findIndex((p) => p.id === selected.id) : -1;
  const tab = (id, Icon, label) => (
    <button type="button" data-testid={`relay-view-${id}`} onClick={() => setView(id)} className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors ${view === id ? "bg-amber-deep text-white" : "text-bark-soft hover:text-amber-deep"}`}>
      <Icon size={12} /> {label}
    </button>
  );

  return (
    <div data-testid="relay-point-picker" className="space-y-2.5 rounded-xl border border-amber-deep/30 bg-amber-50/60 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-bark"><MapPin size={13} className="text-amber-deep" /> Votre Point Relais près du {postal} *</p>
        {points?.length > 0 && <div className="flex shrink-0 rounded-full border border-amber-900/15 bg-white p-0.5">{tab("map", MapIcon, "Carte")}{tab("list", List, "Liste")}</div>}
      </div>
      {points === null ? (
        <p data-testid="relay-points-loading" className="flex items-center gap-2 text-xs text-bark-soft"><Loader2 size={13} className="animate-spin" /> Recherche des Points Relais…</p>
      ) : points.length === 0 ? (
        <p data-testid="relay-points-empty" className="text-xs font-medium text-terra">{error || "Aucun Point Relais trouvé pour ce code postal."}</p>
      ) : view === "map" ? (
        <>
          <RelayMap points={points} selected={selected} onSelect={onSelect} />
          {idx >= 0 ? (
            <PointDetails p={selected} index={idx} selected onSelect={onSelect} open={open === selected.id} setOpen={setOpen} />
          ) : (
            <p data-testid="relay-map-hint" className="text-xs text-bark-soft">Touchez un repère sur la carte pour choisir votre Point Relais.</p>
          )}
        </>
      ) : (
        <ul className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
          {points.map((p, i) => (
            <li key={p.id}><PointDetails p={p} index={i} selected={selected?.id === p.id} onSelect={onSelect} open={open === p.id} setOpen={setOpen} /></li>
          ))}
        </ul>
      )}
    </div>
  );
}
